import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

import { StatusCode } from "api/protocols";
import { PostEmailTemplate } from "api/rpc/2024-04/facilityAdmin/facility/emailTemplate";

import Page from "components/page/Page";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import TextEditor from "components/textEditor/textEditor";
import { Select } from "components/select";
import { useTranslation } from "react-i18next";

interface INewEmailTemplateState {
  title: string;
  subject: string;
  content: string;
  type: "tee_sheet" | "league_announcement";
}

export default function EmailTemplateNew(props: any) {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [newTemplateState, setNewTemplateState] = useState<INewEmailTemplateState>({
    title: "",
    subject: "",
    content: "",
    type: "tee_sheet",
  });

  const templateTypes = [
    { label: t("secure.facility.settings.facility.email_templates.email_templates_new.001"), value: "tee_sheet" },
    {
      label: t("secure.facility.settings.facility.email_templates.email_templates_new.002"),
      value: "league_announcement",
    },
  ];

  async function postTemplate() {
    const templateRes = await PostEmailTemplate(
      {
        title: newTemplateState.title,
        type: newTemplateState.type,
        subject: newTemplateState.subject,
        content: newTemplateState.content,
      },
      true,
    );

    if (templateRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.settings.facility.email_templates.email_templates_new.003")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.settings.facility.email_templates.email_templates_new.004")));
    history.push("/admin/settings/email-template");
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setNewTemplateState(prev => ({ ...prev, [id]: value }));
  }

  const primaryAction = {
    content: t("Create"),
    action: postTemplate,
  };

  return (
    <Page
      title={t("secure.facility.settings.facility.email_templates.email_templates_new.005")}
      narrow
      primaryAction={primaryAction}
      breadcrumbs={[
        {
          prefix: true,
          label: t("secure.facility.settings.facility.email_templates.email_templates_new.006"),
          url: "/admin/settings/email-template",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={newTemplateState.title}
                label={t("secure.facility.settings.facility.email_templates.email_templates_new.007")}
                id="title"
                onChange={handleInputChange}
              />
              <Input
                value={newTemplateState.subject}
                label={t("secure.facility.settings.facility.email_templates.email_templates_new.008")}
                id="subject"
                onChange={handleInputChange}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                defaultValue={newTemplateState.type}
                label={t("secure.facility.settings.facility.email_templates.email_templates_new.009")}
                onChange={value => setNewTemplateState(prev => ({ ...prev, type: value }))}
              >
                {templateTypes.map(type => (
                  <Select.Option key={type.value} value={type.value}>
                    {type.label}
                  </Select.Option>
                ))}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <TextEditor
                markdownText={newTemplateState.content ?? ""}
                markdownTextOnChange={(newContent: string) =>
                  setNewTemplateState(prev => ({ ...prev, content: newContent }))
                }
                label={t("secure.facility.settings.facility.email_templates.email_templates_new.010")}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>
    </Page>
  );
}
